import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type {
  ComplianceDocumentI18nNamespace,
  ComplianceDocumentTranslationKeyPrefix,
} from './complianceDocumentEditorTypes'

export type AddComplianceDocumentSectionDialogProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (sectionTitle: string) => void
  translationKeyPrefix: ComplianceDocumentTranslationKeyPrefix
  i18nNamespace: ComplianceDocumentI18nNamespace
  inputIdPrefix: string
}

export function AddComplianceDocumentSectionDialog({
  isOpen,
  onClose,
  onConfirm,
  translationKeyPrefix,
  i18nNamespace,
  inputIdPrefix,
}: AddComplianceDocumentSectionDialogProps) {
  const { t } = useTranslation(i18nNamespace)
  const [sectionName, setSectionName] = useState('')
  const [showNameRequired, setShowNameRequired] = useState(false)
  const sectionNameInputId = `${inputIdPrefix}-section-name`

  useEffect(() => {
    if (!isOpen) {
      setSectionName('')
      setShowNameRequired(false)
    }
  }, [isOpen])

  const handleConfirm = () => {
    const trimmedName = sectionName.trim()
    if (trimmedName.length === 0) {
      setShowNameRequired(true)
      return
    }
    onConfirm(trimmedName)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>
            {t(`${translationKeyPrefix}.addSectionDialog.title`)}
          </DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground'>
          {t(`${translationKeyPrefix}.addSectionDialog.description`)}
        </p>
        <div className='space-y-2'>
          <Label htmlFor={sectionNameInputId}>
            {t(`${translationKeyPrefix}.addSectionDialog.nameLabel`)}
          </Label>
          <Input
            id={sectionNameInputId}
            value={sectionName}
            autoFocus
            placeholder={t(
              `${translationKeyPrefix}.addSectionDialog.namePlaceholder`
            )}
            onChange={(event) => {
              setSectionName(event.target.value)
              if (showNameRequired && event.target.value.trim().length > 0) {
                setShowNameRequired(false)
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleConfirm()
              }
            }}
          />
          {showNameRequired ? (
            <p className='text-sm text-destructive'>
              {t(`${translationKeyPrefix}.addSectionDialog.nameRequired`)}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            {t(`${translationKeyPrefix}.cancel`)}
          </Button>
          <Button type='button' onClick={handleConfirm}>
            {t(`${translationKeyPrefix}.addSectionDialog.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
